import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useContext, useEffect, useState } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import DateRangePicker from '../../components/shared/date-range-picker';
import Modal from '../../components/shared/modal';
import AddModal from './add-modal';
import ExpenseTable from './expense-table';
import { AccountsContext } from '../../components/context/account-context';
import { getUsersMinimal } from '../../services/users-service';
import { getVehiclesMinimal } from '../../services/vehicles-service';
import { getAllVendorsMinimal } from '../../services/vendors-service';
import { formatUTCTimestampToLocal, getDateRangeUTC } from '../../utils/set-date-range-utils';
import { getAllExpenseCategories, getAllExpensePaymentMethods, getAllExpenses, getAllExpensesStatuses } from '../../services/expenses-service';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import logo from '../../assets/images/logo.jpg';
import { CSVLink } from 'react-csv';
import { getJobsMinimal } from '../../services/jobs-service';
import { camelCaseToWords } from '../../utils/structure-words-utils';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';

const Expenses = () => {
    usePageHelp(Help);
    const { accountId, attributes } = useContext(AccountsContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddModal, setOpenAddModal] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });
    const [newlyAddedItem, setNewlyAddedItem] = useState(null);
    const [expenseData, setExpenseData] = useState([]);
    const [allExpenseData, setAllExpenseData] = useState([]);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [paymentOptions, setPaymentOptions] = useState([]);
    const [jobsOptions, setJobsOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [globalDataloading, setGlobalDataloading] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});

    const fetchGlobalData = async () => {
        if (!accountId) return;
        setGlobalDataloading(true);

        try {
            const [usersRes, vehiclesRes, vendorsRes, categoryRes, paymentRes, jobsRes, statusRes] = await Promise.all([
                getUsersMinimal(accountId),
                getVehiclesMinimal(accountId),
                getAllVendorsMinimal(accountId),
                getAllExpenseCategories(accountId),
                getAllExpensePaymentMethods(accountId),
                getJobsMinimal(accountId),
                getAllExpensesStatuses(accountId),
            ]);

            if (usersRes.success) setUsers(usersRes.data.data || []);
            if (vehiclesRes.success) setVehicles(vehiclesRes.data.data || []);
            if (vendorsRes.success) setVendors(vendorsRes.data.data || []);
            if (categoryRes.success) setCategoryOptions(categoryRes.data.data || []);
            if (paymentRes.success) setPaymentOptions(paymentRes.data.data || []);
            if (jobsRes.success) setJobsOptions(jobsRes.data.data || []);
            if (statusRes.success) setStatusOptions(statusRes.data.data || []);
        } catch (err) {
            console.error("Failed to fetch global data", err);
            setError('Failed to fetch global data');
        } finally {
            setGlobalDataloading(false);
        }
    };

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const { from, to } = getDateRangeUTC(dateRange);

            const response = await getAllExpenses(accountId, from, to);
            console.log("Expense data : ", response)
            if (response.success) {
                const sortedData = response.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)); // sort latest first
                setAllExpenseData(sortedData);
                setExpenseData(sortedData);
                setError(null);
            } else {
                setError(response.data.message || 'Failed to fetch expense records');
            }
        } catch (err) {
            setError('An error occurred while fetching expense records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExpenses();
    }, [accountId, dateRange]);

    const handleOpenAddModal = () => {
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
    };

    const handleAddItem = (newItem) => {
        setNewlyAddedItem(newItem);
        fetchExpenses();
    };

    const handleOpenEditModal = (item) => {
        setNewlyAddedItem(item);
    };

    const handleClearNewlyAddedItem = () => {
        setNewlyAddedItem(null);
    };

    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? expenseData.filter((_, index) => selectedRows[index])
        : expenseData;

    const csvData = exportData.map(item => ({
        Time: item.expenseTime ? formatUTCTimestampToLocal(item.expenseTime, 'yyyy-MM-dd hh:mm a') : '-',
        Vendor: item.vendor?.name ? camelCaseToWords(item.vendor.name) : '----',
        Category: item.category ? camelCaseToWords(item.category) : '----',
        Currency: item.currency || '----',
        Amount: item.amount || '----',
        Spender: item.spender?.name ? camelCaseToWords(item.spender.name) : '----',
        Vehicle: item.vehicle?.name ? camelCaseToWords(item.vehicle.name) : '----',
        Job: item.jobId || '----',
        Status: item.status ? camelCaseToWords(item.status) : '----',
    }));

    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 25;
        const logoHeight = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;

        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title
        const title = 'Expense Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Date Range
        const start = format(dateRange.startDate, 'yyyy-MM-dd');
        const end = format(dateRange.endDate, 'yyyy-MM-dd');
        const dateRangeText = `Date Range: ${start} to ${end}`;
        const dateRangeY = titleY + 5;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const dateRangeWidth = doc.getTextWidth(dateRangeText);
        const dateRangeX = (pageWidth - dateRangeWidth) / 2;
        doc.text(dateRangeText, dateRangeX, dateRangeY);

        // Table
        autoTable(doc, {
            head: [['Time', 'Vendor', 'Category', 'Currency', 'Amount', 'Spender', 'Vehicle', 'Job', 'Status']],
            body: exportData.map(item => [
                item.expenseTime ? formatUTCTimestampToLocal(item.expenseTime, 'yyyy-MM-dd hh:mm a') : '-',
                item.vendor?.name ? camelCaseToWords(item.vendor.name) : '----',
                item.category ? camelCaseToWords(item.category) : '----',
                item.currency || '----',
                item.amount ? `${item.amount}` : '----',
                item.spender?.name ? camelCaseToWords(item.spender.name) : '----',
                item.vehicle?.name ? camelCaseToWords(item.vehicle.name) : '----',
                item.jobId || '----',
                item.status ? camelCaseToWords(item.status) : '----',
            ]),
            startY: dateRangeY + 5,
        });

        // Filename
        const fileName = `expenses-${start}-to-${end}.pdf`;
        doc.save(fileName);
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;


    return (
        <div className="px-6 py-4 ">

            <div className='flex mb-2 items-center justify-between'>
                <div className=''>
                    <Heading>Expense History</Heading>
                </div>
                <Button
                    label='Add Expense'
                    onClick={handleOpenAddModal}
                    className='text-white px-4 !py-1 rounded-lg bg-btnBg'
                />
            </div>

            <div className='space-y-4' >

                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <DateRangePicker onChange={setDateRange} />

                    </div>

                    {/* Export buttons - added here */}
                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                filename={`Expenses-${format(dateRange.startDate, 'yyyy-MM-dd')}-to-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`}
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <ExpenseTable
                    data={expenseData}
                    setData={setExpenseData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    newlyAddedItem={newlyAddedItem}
                    setNewlyAddedItem={setNewlyAddedItem}
                    onClearNewlyAddedItem={handleClearNewlyAddedItem}
                    fetchExpenses={fetchExpenses}
                    loadingData={loading}
                    accountId={accountId}
                    spendors={users}
                    vendors={vendors}
                    vehicles={vehicles}
                    statusOptions={statusOptions}
                    categoryOptions={categoryOptions}
                    paymentOptions={paymentOptions}
                    jobsOptions={jobsOptions}
                    attributes={attributes}
                    fetchGlobalData={fetchGlobalData}
                    globalDataloading={globalDataloading}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                />

                <Modal
                    isOpen={openAddModal}
                    onClose={handleCloseAddModal}
                    modalClass={'xl:w-[40%] lg:w-[45%] md:w-[65%] sm:w-[70%] w-[90%] !px-0'}
                >
                    <AddModal
                        handleCloseAddModal={handleCloseAddModal}
                        onAdd={handleAddItem}
                        openEditModal={handleOpenEditModal}
                        spendors={users}
                        vendors={vendors}
                        vehicles={vehicles}
                        categories={categoryOptions}
                        paymentMethods={paymentOptions}
                        jobsOptions={jobsOptions}
                        fetchGlobalData={fetchGlobalData}
                        globalDataloading={globalDataloading}
                    />
                </Modal>
            </div>
        </div >
    );
};

export default Expenses;